// ============================================================
// IssuedStatement.tsx — (B4, batch 2 read-first) an ISSUED settlement
// statement: a read-only summary card (number / version, status, issued by /
// when, signed by, who it's addressed to, the net + where it goes, delivery,
// fingerprint) with View PDF / Send… / Correct; then the court steps (child /
// protected party) or the client's answer (adults — a button opens the form,
// once recorded a readable summary + "Change answer"); then the B4b hand-off.
// Also the version list.
// (B4c batch 1b) The online-signing status (statement/EsignStatus): signed →
// "Signed online by … — View signed PDF · Evidence pack"; open → sent /
// opened / code verified with Resend link + Cancel request.
// (E-sign fixes B) The e-sign status shows even with the pilot off (no
// buttons that would fail — it says why), with the last e-sign refusal under
// it. The figure tiles wrap by the room they have (FIGURE_GRID): at 360 px /
// 200% zoom the Net sum tile gets a full row — no clipped £ amounts.
// (D12) A paper request opens the Send window on Posted or handed over; "not
// me" offers a new request; the redacted evidence pack sits beside the pack.
// The card carries data-statement-id (the bell's statement links scroll to it).
// (Big-audit fix batch 1) A request fully signed on an earlier version shows
// here too: "Signed on an earlier version (version N) — on <date>", with its
// signed PDF and evidence packs.
// (Big-audit fix batch 2) Once THIS version is signed too, every earlier
// signing folds into one "Earlier signings (N)" line that opens to show them
// (a name to check inside is flagged on the folded line). Not signed yet →
// the earlier cards show as before.
// ============================================================
import React from 'react'
import type { recordClientApprovalApi } from '../../../api/casevoApi'
import {
  netSumLabel,
  type CaseStatements, type RepresentativeType, type StatementSummary,
} from '../../../types/settlementStatement.types'
import { B_SECONDARY_SM, BIcon, NOTE, money } from '../../billing/billingUi'
import { Banner } from '../settlementUi'
import ActionBox from '../../shared/ActionBox'
import { ukDate } from '../../../utils/civilDate'
import { CHIP, FIGURE_GRID, Figure, StatusChip, SummaryRow, shortHash, stRef } from './statementBits'
import { CourtSteps, type RecordStage } from './CourtApproval'
import EsignStatus, { EarlierSignings, isFullySigned, nameMismatchLine } from './EsignStatus'
import { ClientAnswer, type LaterActions } from './ClientAnswer'
import type { StatementEsignSummary } from '../../../types/esign.types'

// (B4c 1b) What the statement card can do about an e-signature request.
export interface EsignActions {
  resend: () => void
  cancel: (reason: string) => Promise<boolean>
  viewSigned: () => void
  evidencePack: () => void
  // (D12)
  redactedPack?: () => void
  sendPaper?: () => void
  sendNew?: () => void
}

type ApprovalBody = Parameters<typeof recordClientApprovalApi>[2]
const DELIVERY: Record<'EMAIL' | 'POST' | 'HAND', string> = { EMAIL: 'Emailed', POST: 'Posted', HAND: 'Handed over' }

// Who the statement goes to: the litigation friend for a child / protected party.
export const addresseeOf = (s: StatementSummary, data: Pick<CaseStatements, 'litigationFriendName'>): string | null =>
  s.addressee?.name ?? (s.capacity !== 'ADULT' && data.litigationFriendName ? `${data.litigationFriendName} (litigation friend)` : null)

// (Big-audit fix batch 2) A request fully signed on an earlier version.
export interface EarlierSigned { version: number; e: StatementEsignSummary; actions: EsignActions; busy: string | null; error: string | null }

export const IssuedCard: React.FC<{
  s: StatementSummary; data: CaseStatements; busy: string | null; hasDraft: boolean
  preview: () => void; send: () => void; correct: () => Promise<boolean>
  approve: (body: ApprovalBody) => Promise<boolean>
  // (Big-audit batch 2, M1) After an e-signed answer: a later instruction (record / approve / refuse).
  later?: LaterActions
  caseId?: string
  recordStage: RecordStage
  esignActions?: EsignActions
  // (E-sign fixes B) The firm's pilot is off; the last e-sign action's refusal.
  esignPilotOff?: boolean
  esignError?: string | null
  // (Big-audit fix batch 1) The busy key as the e-sign status should see it
  // (another request's action → disabled, not "busy"); a request fully signed
  // on an earlier version of this statement.
  esignBusy?: string | null
  // (Big-audit fix batch 2) …or several (every earlier version signed online).
  earlierSigned?: EarlierSigned | EarlierSigned[] | null
}> = ({ s, data, busy, hasDraft, preview, send, correct, approve, later, caseId, recordStage, esignActions, esignPilotOff = false, esignError = null, esignBusy, earlierSigned = null }) => {
  const childOrPp = s.capacity !== 'ADULT'
  const esign = data.esign?.[s.id] ?? null
  const to = addresseeOf(s, data)
  // (Big-audit fix batch 2) The earlier signings, newest first; folded into
  // one "Earlier signings (N)" line once THIS version is signed.
  const earlier = (Array.isArray(earlierSigned) ? earlierSigned : earlierSigned ? [earlierSigned] : []).slice().sort((a, b) => b.version - a.version)
  const earlierCards = earlier.map((x) => (
    <EsignStatus key={x.e.id} e={x.e} busy={x.busy} earlierVersion={x.version}
      onResend={x.actions.resend} onCancel={x.actions.cancel} onViewSigned={x.actions.viewSigned}
      onEvidencePack={x.actions.evidencePack} onRedactedPack={x.actions.redactedPack}
      pilotOff={esignPilotOff} error={x.error} />
  ))
  return (
    <section aria-label={`Statement ${stRef(s)}`} data-statement-id={s.id} tabIndex={-1} className="scroll-mt-[16px] focus:outline-none rounded-[12px] border border-casevo-border px-[14px] py-[12px] flex flex-col gap-3">
      <div className="flex items-center gap-[10px] flex-wrap">
        <h4 className="m-0 text-[14.5px] font-semibold text-casevo-forest tabular-nums">{stRef(s)}</h4>
        <StatusChip status={s.status} />
        {s.proposed && <span className={`${CHIP} border-casevo-warn-border bg-casevo-warn-bg text-casevo-warn`}>Proposed</span>}
      </div>
      <dl className="m-0 grid gap-[10px] sm:grid-cols-3" aria-label="Statement summary">
        <SummaryRow label="Issued">{s.issuedAt ? ukDate(s.issuedAt.slice(0, 10)) : '—'}{s.issuedBy ? ` by ${s.issuedBy}` : ''}</SummaryRow>
        <SummaryRow label="Signed by">{s.signatoryName ?? '—'}</SummaryRow>
        {to && <SummaryRow label="Addressed to">{to}</SummaryRow>}
        {/* (Follow-ups) The net sum is shown ONCE — in the figures below; here only its payment detail. */}
        {s.netDestinationDetail ? <SummaryRow label="Where the net sum goes">{s.netDestinationDetail}</SummaryRow> : null}
        <SummaryRow label="Delivered">{s.delivered ? `${DELIVERY[s.delivered.method]} ${ukDate(String(s.delivered.at).slice(0, 10))}` : 'Not sent yet'}</SummaryRow>
        <SummaryRow label="Fingerprint"><span className="tabular-nums" title={s.contentHash ?? undefined}>{shortHash(s.contentHash)}</span></SummaryRow>
        {/* (Audit fix batch 1, H2) The final bill differed and someone checked it: who, when, why. */}
        {s.billMismatchOverride && (
          <SummaryRow label={`Differs from bill ${s.billMismatchOverride.billNumber}`}>
            <span data-mismatch-override>Checked by {s.billMismatchOverride.by}{s.billMismatchOverride.at ? ` on ${ukDate(String(s.billMismatchOverride.at).slice(0, 10))}` : ''} — ‘{s.billMismatchOverride.reason}’</span>
            {s.billMismatchOverride.parts.length > 0 && (
              <span className="block text-[12.5px] text-casevo-text-soft">{s.billMismatchOverride.parts.map((p) => `${p.label}: bill ${money(p.billPence)} · statement ${money(p.statementPence)}`).join('; ')}</span>
            )}
          </SummaryRow>
        )}
      </dl>
      <div className={FIGURE_GRID} data-figures="">
        <Figure label="Damages" pence={s.figures.totalDamagesPence} />
        <Figure label="Deductions" pence={s.figures.totalDeductionsPence} />
        <Figure label="Net sum" pence={s.figures.netToClientPence} strong sub={netSumLabel(s.netDestination, data.representativeType)} />
      </div>
      {s.proposed && <Banner tone="info">This is a PROPOSED statement for the court’s approval. Once the order is recorded, start the final statement — it replaces this one.</Banner>}
      {s.notesReviewed && s.notesReviewed.notes.length > 0 && <p className={`${NOTE} m-0`}>Notes reviewed by {s.notesReviewed.by} on {ukDate(String(s.notesReviewed.at).slice(0, 10))}: {s.notesReviewed.notes.map((n) => n.message).join(' ')}</p>}
      <div className="flex items-center gap-[8px] flex-wrap">
        <button type="button" className={B_SECONDARY_SM} disabled={!!busy} onClick={preview}><BIcon name="eye" size={12} />View PDF</button>
        <button type="button" className={B_SECONDARY_SM} disabled={!!busy} onClick={send}><BIcon name="send" size={12} />Send…</button>
        <button type="button" className={B_SECONDARY_SM} disabled={!!busy || hasDraft} onClick={() => { void correct() }}><BIcon name="pen" size={12} />{s.proposed ? 'Start the final statement' : 'Correct (new version)'}</button>
        {hasDraft && <span className={NOTE}>Finish the draft above first.</span>}
        {!s.pdfSaved && <span className="text-[12px] text-casevo-warn">The PDF wasn’t saved to the case yet — it’s made when you view or send it.</span>}
      </div>
      {esign && esignActions && (
        <EsignStatus e={esign} busy={esignBusy === undefined ? busy : esignBusy} onResend={esignActions.resend} onCancel={esignActions.cancel} onViewSigned={esignActions.viewSigned} onEvidencePack={esignActions.evidencePack}
          onRedactedPack={esignActions.redactedPack} onSendPaper={esignActions.sendPaper} onSendNew={esignActions.sendNew}
          pilotOff={esignPilotOff} error={esignError} />
      )}
      {earlier.length > 0 && (isFullySigned(esign)
        ? <EarlierSignings count={earlier.length} versions={earlier.map((x) => x.version)} mismatch={earlier.some((x) => !!nameMismatchLine(x.e))}>{earlierCards}</EarlierSignings>
        : earlierCards)}
      {childOrPp
        ? <CourtSteps stages={data.courtStages} history={s.courtStageHistory} current={s.courtStage} busy={busy} today={data.today} onRecord={recordStage} />
        : <ClientAnswer s={s} today={data.today} busy={busy} onSubmit={approve} onRecordDelivery={send}
            caseId={caseId} later={later} viewerUserId={data.viewerUserId} canCheck={data.canCheck} onlyApprover={data.onlyApprover} />}
      <HandOff s={s} representativeType={data.representativeType} />
    </section>
  )
}

// ── The client's answer (adults) ─────────────────────────────
// (Big-audit fix batch 2, M1) Moved to ./ClientAnswer (manual answers, the
// e-signed answer's later instructions, the answer history).
export { ANSWER_NEEDS_DELIVERY, ClientAnswer, ClientApprovalForm } from './ClientAnswer'

// ── The hand-off to client account (B4b) ─────────────────────
export const HandOff: React.FC<{ s: StatementSummary; representativeType?: RepresentativeType | null }> = ({ s, representativeType }) => {
  const r = s.readiness
  return (
    // (Audit fix batch 2) Green = done (ready), else neutral information.
    <ActionBox ariaLabel="Client account hand-off" tone={r.ready ? 'done' : 'info'} title={r.label} message={!r.ready && r.reason ? r.reason : undefined}>
      {r.ready && (
        <dl className={`m-0 mt-[6px] ${FIGURE_GRID} text-[12.5px]`}>
          <div className="min-w-0"><dt className="text-casevo-text-soft break-words">{netSumLabel(s.netDestination, representativeType)}</dt><dd className="m-0 font-semibold tabular-nums">{money(r.netToClientPence)}</dd></div>
          <div className="min-w-0"><dt className="text-casevo-text-soft break-words">Approved deductions</dt><dd className="m-0 font-semibold tabular-nums">{money(r.approvedDeductionsPence)}</dd></div>
          <div className="min-w-0"><dt className="text-casevo-text-soft break-words">Held (disputed)</dt><dd className="m-0 font-semibold tabular-nums">{money(r.heldPence)}</dd></div>
        </dl>
      )}
      <p className={`${NOTE} m-0 mt-[4px]`}>No money moves yet — the client-account transfers come with the legal-accounts work.</p>
    </ActionBox>
  )
}

// ── Versions ─────────────────────────────────────────────────
export const VersionList: React.FC<{ statements: StatementSummary[]; busy: string | null; onPreview: (s: StatementSummary) => void; representativeType?: RepresentativeType | null }> = ({ statements, busy, onPreview, representativeType }) => (
  <div aria-label="Statement versions">
    <h5 className="m-0 mb-[6px] text-[12px] font-semibold uppercase tracking-[0.08em] text-casevo-text-soft">Versions</h5>
    <ul className="m-0 p-0 list-none flex flex-col divide-y divide-casevo-border border border-casevo-border rounded-[10px]">
      {statements.map((s) => (
        <li key={s.id} className="flex items-center gap-[10px] px-[12px] py-[8px] text-[13px] flex-wrap" data-version={s.version}>
          <b className="tabular-nums">{stRef(s)}</b>
          <StatusChip status={s.status} />
          {s.proposed && <span className="text-[12px] text-casevo-warn">Proposed</span>}
          {s.supersededBy && <span className="text-[12px] text-casevo-warn">Superseded by v{s.supersededBy.version}</span>}
          {s.supersedes && <span className="text-[12px] text-casevo-text-soft">Replaces v{s.supersedes.version}</span>}
          <span className="text-casevo-text-soft">{s.issuedAt ? ukDate(s.issuedAt.slice(0, 10)) : ''}</span>
          <span className="flex-1" />
          <span className="tabular-nums">{netSumLabel(s.netDestination, representativeType)} {money(s.figures.netToClientPence)}</span>
          <button type="button" className={B_SECONDARY_SM} disabled={!!busy} onClick={() => onPreview(s)}>View</button>
        </li>
      ))}
    </ul>
  </div>
)
